/**
 * Renewals tab (#1142): what renews when, and what it does to cost and margin. A 12-month customer swimlane (dot size =
 * what we bill a month on those lines, colour = margin band once Pax8's renewal price applies) above a month-grouped
 * table. Month-to-month and internal lines are hidden unless added.
 */
import { cn, EmptyState, Tooltip } from '@patchbay/ui'
import * as React from 'react'
import type { LicenceMarginRow } from '../../../lib/api/generated'
import {
  ChipButton,
  ChipFilter,
  type Column,
  cellBase,
  countStat,
  GroupedTable,
  groupClasses,
  MarginValue,
  MaybeLink,
  Missing,
  moneyStat,
  numCell,
  SearchBox,
  type Stat,
  StatStrip,
} from './bits'
import {
  billedMonthly,
  commitment,
  type DotTone,
  dayLabel,
  daysUntil,
  dotTone,
  isCommit,
  MARGIN_BANDS,
  marginAtRenewal,
  matchesSearch,
  money,
  monthKey,
  monthLabel,
  type Notes,
  num,
  qtyText,
  RENEWAL_CHIPS,
  RENEWAL_WINDOWS,
  type RenewalWindow,
  renewalCost,
  renewalDelta,
  renewalLines,
  renewalRows,
  renewalSummary,
  type SortState,
  signed,
  sortBy,
  timelineSpan,
  total,
  worstDot,
} from './model'
import { NoteCell } from './notes'

export const lineId = (r: LicenceMarginRow): string => r.key ?? `${r.customer}|${r.product}|${r.renews ?? ''}`

const DOT_CLASS: Record<DotTone, string> = {
  negative: 'bg-destructive border-card',
  u5: 'bg-warning border-card',
  // Under 10% is the hollow warning dot, so it never relies on a second shade of amber.
  u10: 'bg-card border-warning',
  u20: 'bg-subtle border-card',
  ok: 'bg-success border-card',
  internal: 'bg-primary border-card',
  unknown: 'bg-card border-border-strong border-dashed',
}
const DOT_LABEL: Record<DotTone, string> = {
  ...(Object.fromEntries(MARGIN_BANDS.map((b) => [b.band, b.label])) as Record<(typeof MARGIN_BANDS)[number]['band'], string>),
  internal: 'Internal',
  unknown: 'Margin unknown',
}

const shortMoney = (n: number) => {
  const a = Math.abs(n)
  const s = a >= 1000 ? `$${(a / 1000).toFixed(a >= 10_000 ? 0 : 1)}K` : `$${Math.round(a)}`
  return n < 0 ? `−${s}` : s
}

function Timeline({
  rows,
  asOf,
  month,
  onMonth,
  onDot,
  onName,
}: {
  rows: LicenceMarginRow[]
  asOf: number
  month: string | null
  onMonth: (key: string) => void
  onDot: (row: LicenceMarginRow) => void
  onName: (customer: string) => void
}) {
  const span = React.useMemo(() => timelineSpan(asOf), [asOf])
  const todayX = span.x(asOf)
  const byCustomer = new Map<string, LicenceMarginRow[]>()
  for (const r of rows) byCustomer.set(r.customer, [...(byCustomer.get(r.customer) ?? []), r])
  const dotValue = (r: LicenceMarginRow) => billedMonthly(r) ?? renewalCost(r)
  const maxV = Math.max(1, ...rows.map((r) => dotValue(r) ?? 0))
  const customers = [...byCustomer.entries()].sort(
    ([a, ar], [b, br]) =>
      (ar.map((r) => r.renews ?? '').sort()[0] ?? '').localeCompare(br.map((r) => r.renews ?? '').sort()[0] ?? '') || a.localeCompare(b),
  )
  const grid = (
    <>
      {span.months.map((m) => (
        <div
          key={m.key}
          aria-hidden
          className={cn('pointer-events-none absolute inset-y-0 border-l border-border', month === m.key && 'bg-primary-soft/60')}
          style={{ left: `${m.a}%`, width: `${m.b - m.a}%` }}
        />
      ))}
      <div aria-hidden className="pointer-events-none absolute inset-y-0 z-[1] border-l-2 border-primary" style={{ left: `${todayX}%` }} />
    </>
  )
  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-card">
      <div className="min-w-[760px]" data-timeline>
        <div className="sticky top-0 z-[3] grid grid-cols-[8.5rem_1fr] border-b border-border bg-card sm:grid-cols-[13rem_1fr]">
          <div className="border-r border-border px-2.5 py-1.5 text-xs text-muted-foreground">
            {rows.length ? 'Customer' : 'Nothing renews in this window'}
          </div>
          <div className="relative min-h-14">
            {span.months.map((m) => {
              const s = renewalSummary(rows.filter((r) => r.renews && monthKey(r.renews) === m.key))
              const up = s.change.value ?? 0
              return (
                <button
                  key={m.key}
                  type="button"
                  aria-pressed={month === m.key}
                  data-month={m.key}
                  aria-label={`${monthLabel(m.key, true)}: ${s.n} renewal${s.n === 1 ? '' : 's'}${s.billed.value === null ? '' : `, ${money(s.billed.value)} a month billed`}${up > 0.5 ? `, cost up ${money(up)} a month` : ''}`}
                  onClick={() => onMonth(m.key)}
                  className={cn(
                    'absolute inset-y-0 flex cursor-pointer flex-col gap-px overflow-hidden border-l border-border px-1.5 py-1 text-left hover:bg-accent focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring',
                    month === m.key && 'bg-primary-soft shadow-[inset_0_-2px_0_var(--color-primary)]',
                  )}
                  style={{ left: `${m.a}%`, width: `${m.b - m.a}%` }}
                >
                  <span className="text-xs font-semibold">{monthLabel(m.key)}</span>
                  <span className="tabular text-2xs whitespace-nowrap text-muted-foreground">
                    {s.n ? `${s.n} · ${s.billed.value === null ? '?' : shortMoney(s.billed.value)}` : '–'}
                  </span>
                  {up > 0.5 ? <span className="tabular text-2xs whitespace-nowrap text-destructive">+{shortMoney(up)} cost</span> : null}
                </button>
              )
            })}
            <div
              aria-hidden
              title="Today"
              className="pointer-events-none absolute inset-y-0 z-[1] border-l-2 border-primary"
              style={{ left: `${todayX}%` }}
            />
          </div>
        </div>
        {customers.map(([customer, rs]) => {
          const byDay = new Map<string, LicenceMarginRow[]>()
          for (const r of rs) if (r.renews) byDay.set(r.renews, [...(byDay.get(r.renews) ?? []), r])
          return (
            <div
              key={customer}
              className="grid grid-cols-[8.5rem_1fr] border-b border-border last:border-0 sm:grid-cols-[13rem_1fr]"
              data-lane={customer}
            >
              <div className="truncate border-r border-border px-2.5 py-1.5 text-xs">
                <button
                  type="button"
                  title={customer}
                  onClick={() => onName(customer)}
                  className="cursor-pointer hover:text-primary focus-visible:outline-2 focus-visible:outline-ring"
                >
                  {customer}
                </button>
              </div>
              <div className="relative min-h-8">
                {grid}
                {[...byDay.entries()].map(([day, ds]) => {
                  const v = total(ds, dotValue).value
                  const size = Math.round(12 + 20 * Math.sqrt(Math.min(1, (v ?? 0) / maxV)))
                  const tone = worstDot(ds.map(dotTone))
                  const d = daysUntil(day, asOf)
                  const lines = [
                    `${customer} · ${dayLabel(day)} (${d === 0 ? 'today' : `in ${d} day${d === 1 ? '' : 's'}`})`,
                    `Margin at renewal: ${DOT_LABEL[tone]}`,
                    ...ds.flatMap((r) => ['', ...renewalLines(r)]),
                  ]
                  const dim = month !== null && monthKey(day) !== month
                  return (
                    <Tooltip
                      key={day}
                      content={
                        <div className="grid max-w-80 gap-0.5 text-left" data-testid="dot-detail">
                          {lines.map((l, i) =>
                            // biome-ignore lint/suspicious/noArrayIndexKey: detail lines are positional
                            l ? <div key={i}>{l}</div> : <div key={i} className="h-1" />,
                          )}
                        </div>
                      }
                    >
                      <button
                        type="button"
                        data-dot={day}
                        data-tone={tone}
                        aria-label={lines.filter(Boolean).join('. ')}
                        onClick={() => onDot(ds[0] as LicenceMarginRow)}
                        className={cn(
                          'absolute top-1/2 z-[2] -translate-x-1/2 -translate-y-1/2 cursor-pointer rounded-full border-2 hover:outline-2 hover:outline-offset-1 hover:outline-foreground focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-foreground',
                          DOT_CLASS[tone],
                          dim && 'opacity-20',
                        )}
                        style={{ left: `${span.x(Date.parse(`${day}T00:00:00Z`))}%`, width: size, height: size }}
                      />
                    </Tooltip>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const COLUMNS: Column[] = [
  { key: 'renews', label: 'Renews', group: '' },
  { key: 'customer', label: 'Customer', group: '' },
  { key: 'product', label: 'Licence', group: '' },
  { key: 'pax8_qty', label: 'Seats', group: '', numeric: true },
  { key: 'billed', label: 'We bill', group: 'Per month', numeric: true },
  { key: 'cost_now', label: 'Cost now', group: 'Per month', numeric: true },
  { key: 'cost_renewal', label: 'At renewal', group: 'Per month', numeric: true },
  { key: 'change', label: 'Change', group: 'Per month', numeric: true },
  { key: 'margin', label: 'Margin', group: 'At renewal', numeric: true },
  { key: 'commit', label: '12-mo commit', group: 'At renewal', numeric: true, narrow: true },
  { key: null, label: 'Notes', group: ' ' },
]

function sortValue(r: LicenceMarginRow, key: string): string | number | null | undefined {
  switch (key) {
    case 'pax8_qty':
      return r.pax8_qty
    case 'billed':
      return billedMonthly(r)
    case 'cost_now':
      return num(r.monthly_cost_now)
    case 'cost_renewal':
      return num(r.monthly_cost_at_renewal)
    case 'change':
      return renewalDelta(r)
    case 'margin':
      return marginAtRenewal(r)
    case 'commit':
      return commitment(r)
    default:
      return r[key as keyof LicenceMarginRow] as string | null | undefined
  }
}

const maybeMoney = (n: number | null) => (n === null ? <Missing /> : money(n))

function monthLine(rows: LicenceMarginRow[]): string {
  const s = renewalSummary(rows)
  const parts = [`${s.n} renewal${s.n === 1 ? '' : 's'}`]
  parts.push(s.billed.value === null ? 'billed unknown' : `${money(s.billed.value)}/mo billed`)
  const up = s.change.value
  parts.push(
    s.cost.value === null
      ? 'cost at renewal unknown'
      : `cost ${money(s.cost.value)}/mo at renewal${up !== null && Math.abs(up) > 0.05 ? ` (${signed(up, money)})` : ''}`,
  )
  if (s.negative) parts.push(`${s.negative} negative`)
  return parts.join(' · ')
}

function RenewalRow({
  row,
  notes,
  asOf,
  cls,
  flash,
}: {
  row: LicenceMarginRow
  notes: Notes
  asOf: number
  cls: string[]
  flash: boolean
}) {
  const c = (i: number, extra?: string) => cn(cellBase, cls[i], extra)
  const d = row.renews ? daysUntil(row.renews, asOf) : null
  const change = renewalDelta(row)
  const billed = billedMonthly(row)
  const margin = marginAtRenewal(row)
  return (
    <tr data-renewal-line={lineId(row)} className={cn('transition-colors duration-1000', flash && 'bg-primary-soft')}>
      <td className={c(0, 'tabular whitespace-nowrap')}>
        {row.renews ? dayLabel(row.renews) : <Missing />}
        {d !== null ? (
          <span className="block text-xs text-muted-foreground">{d === 0 ? 'today' : `in ${d} day${d === 1 ? '' : 's'}`}</span>
        ) : null}
      </td>
      <td className={c(1, 'font-medium')}>
        <MaybeLink href={row.links?.crm}>{row.customer}</MaybeLink>
      </td>
      <td className={c(2, 'min-w-52')}>
        <MaybeLink href={row.links?.pax8}>{row.product}</MaybeLink>
        {row.status === 'Internal' ? (
          <span className="ml-1.5 rounded-sm bg-audience-internal-soft px-1 text-2xs font-medium text-audience-internal">Internal</span>
        ) : null}
        {row.term ? <span className="block text-xs text-muted-foreground">{row.term}</span> : null}
      </td>
      <td className={c(3, numCell)}>{row.pax8_qty == null ? <Missing /> : qtyText(row.pax8_qty)}</td>
      <td className={c(4, numCell)}>
        {billed === null ? (
          row.status === 'Internal' ? (
            <Missing words="internal, not billed" />
          ) : (
            <Missing />
          )
        ) : (
          <MaybeLink href={row.links?.books}>{money(billed)}</MaybeLink>
        )}
      </td>
      <td className={c(5, numCell)}>{maybeMoney(num(row.monthly_cost_now))}</td>
      <td className={c(6, numCell)}>{maybeMoney(num(row.monthly_cost_at_renewal))}</td>
      <td
        className={c(
          7,
          cn(numCell, change !== null && change > 0.05 && 'text-destructive', change !== null && change < -0.05 && 'text-success'),
        )}
        data-change
      >
        {change === null ? (
          <Missing />
        ) : Math.abs(change) > 0.05 ? (
          signed(change, money)
        ) : (
          <span className="text-xs text-muted-foreground">no change</span>
        )}
      </td>
      <td className={c(8, numCell)}>
        <MarginValue value={margin} lines={margin === null ? [] : renewalLines(row).slice(1)} />
      </td>
      <td className={c(9, numCell)}>
        {isCommit(row) ? maybeMoney(commitment(row)) : <span className="text-xs text-muted-foreground">monthly</span>}
      </td>
      <td className={c(10)}>
        <NoteCell note={row.key ? notes[row.key] : undefined} editHref={row.links?.patchbay} />
      </td>
    </tr>
  )
}

export function RenewalsTab({ rows, notes, asOf }: { rows: LicenceMarginRow[]; notes: Notes; asOf: number }) {
  const [win, setWin] = React.useState<RenewalWindow>('365')
  const [chip, setChip] = React.useState('all')
  const [q, setQ] = React.useState('')
  const [month, setMonth] = React.useState<string | null>(null)
  const [m2m, setM2m] = React.useState(false)
  const [internal, setInternal] = React.useState(false)
  const [sort, setSort] = React.useState<SortState>({ key: 'renews', dir: 1 })
  const [flash, setFlash] = React.useState<string | null>(null)

  const days = RENEWAL_WINDOWS.find((w) => w.id === win)?.days ?? 366
  const year = React.useMemo(() => renewalRows(rows, asOf, { days: 366, m2m, internal }), [rows, asOf, m2m, internal])
  const inWindow = year.filter((r) => r.renews && daysUntil(r.renews, asOf) < days)
  const def = RENEWAL_CHIPS.find((d) => d.id === chip) ?? (RENEWAL_CHIPS[0] as (typeof RENEWAL_CHIPS)[number])
  const filtered = inWindow.filter(
    (r) => def.test(r, notes) && matchesSearch([r.customer, r.product, r.books_customer, r.key ? notes[r.key]?.text : null], q),
  )
  const tableRows = sortBy(
    filtered.filter((r) => !month || (r.renews && monthKey(r.renews) === month)),
    sort,
    sortValue,
  )

  const s30 = renewalSummary(year.filter((r) => r.renews && daysUntil(r.renews, asOf) < 30))
  const s90 = renewalSummary(year.filter((r) => r.renews && daysUntil(r.renews, asOf) < 90))
  const sw = renewalSummary(inWindow)
  const commits = total(inWindow.filter(isCommit), commitment)
  const change = sw.change.value
  const stats: Stat[] = [
    countStat('Renewing in 30 days', s30.n),
    moneyStat('Billed / mo, 30 days', s30.billed),
    countStat('Renewing in 90 days', s90.n),
    moneyStat('Billed / mo, 90 days', s90.billed),
    {
      label: 'Cost change at renewal',
      value: change === null ? null : `${signed(change, money)}/mo`,
      tone: change !== null && change > 0 ? 'warn' : undefined,
      note:
        change !== null && sw.change.missing
          ? `${sw.change.missing} line${sw.change.missing === 1 ? '' : 's'} without a renewal price`
          : null,
    },
    countStat('Negative at renewal', sw.negative, 'bad'),
    moneyStat('12-mo commitments in view', commits),
  ]

  // A dot jumps to its line: clear the month and the chip so the line is listed, then scroll to it and flash it.
  React.useEffect(() => {
    if (!flash) return
    const el = document.querySelector(`[data-renewal-line="${CSS.escape(flash)}"]`)
    el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
    const t = window.setTimeout(() => setFlash(null), 1600)
    return () => window.clearTimeout(t)
  }, [flash])

  const cls = groupClasses(COLUMNS)
  const byMonth = sort.key === 'renews'
  const body: React.ReactNode[] = []
  let last: string | null = null
  for (const r of tableRows) {
    const m = r.renews ? monthKey(r.renews) : ''
    if (byMonth && m !== last) {
      last = m
      body.push(
        <tr key={`month:${m}`} data-month-row={m} className="bg-muted">
          <td colSpan={COLUMNS.length} className="border-b border-border px-2 py-1.5 text-xs font-semibold">
            {m ? monthLabel(m, true) : 'No renewal date'}
            <span className="ml-2 font-normal text-muted-foreground">
              {monthLine(tableRows.filter((x) => (x.renews ? monthKey(x.renews) : '') === m))}
            </span>
          </td>
        </tr>,
      )
    }
    body.push(<RenewalRow key={lineId(r)} row={r} notes={notes} asOf={asOf} cls={cls} flash={flash === lineId(r)} />)
  }

  return (
    <div className="flex flex-col gap-3.5">
      <StatStrip label="Renewal totals" stats={stats} />
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <fieldset className="flex flex-wrap items-center gap-1.5" aria-label="Renewal window">
          <legend className="sr-only">Renewal window</legend>
          {RENEWAL_WINDOWS.map((w) => (
            <ChipButton
              key={w.id}
              pressed={win === w.id}
              onClick={() => {
                setWin(w.id)
                setMonth(null)
              }}
            >
              {w.label}
            </ChipButton>
          ))}
        </fieldset>
        <ChipFilter label="Filter renewals" defs={RENEWAL_CHIPS} rows={inWindow} notes={notes} value={chip} onChange={setChip} />
      </div>
      <fieldset className="flex flex-wrap items-center gap-1.5" aria-label="Also show">
        <legend className="mr-1 float-left text-xs text-muted-foreground">Also show:</legend>
        <ChipButton pressed={m2m} onClick={() => setM2m(!m2m)}>
          Month-to-month
        </ChipButton>
        <ChipButton pressed={internal} onClick={() => setInternal(!internal)}>
          Internal
        </ChipButton>
      </fieldset>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground" data-legend>
        <span>Margin at renewal</span>
        {(['negative', 'u5', 'u10', 'u20', 'ok', 'internal', 'unknown'] as DotTone[]).map((t) => (
          <span key={t} className="inline-flex items-center gap-1.5">
            <span aria-hidden className={cn('inline-block size-2.5 rounded-full border-2', DOT_CLASS[t])} />
            {DOT_LABEL[t]}
          </span>
        ))}
        <span>Hover a dot for the lines.</span>
      </div>
      <Timeline
        rows={filtered}
        asOf={asOf}
        month={month}
        onMonth={(k) => setMonth(month === k ? null : k)}
        onDot={(r) => {
          setMonth(null)
          setChip('all')
          setFlash(lineId(r))
        }}
        onName={setQ}
      />
      <SearchBox
        label="Search renewals"
        value={q}
        onChange={setQ}
        count={`${tableRows.length} renewal${tableRows.length === 1 ? '' : 's'}${month ? ` in ${monthLabel(month, true)} · click the month again to show all` : ''}`}
      />
      {tableRows.length === 0 ? (
        <EmptyState
          title="Nothing renews in this view."
          detail="Widen the window, add month-to-month or internal lines, or clear the search."
        />
      ) : (
        <GroupedTable label="Renewals" columns={COLUMNS} sort={sort} onSort={setSort} minWidth={1050}>
          {body}
        </GroupedTable>
      )}
      <p className="max-w-[80ch] text-xs text-muted-foreground">
        Each dot is one renewal date for a customer: size is what we bill a month for those licences, colour is the margin once Pax8's
        renewal price applies. Click a month to list just that month, a dot to jump to its lines, a name to search for it. Month-to-month
        subscriptions renew every month, so they are hidden unless you add them. 12-month commitment is the Pax8 cost at the renewal price
        for a year, the amount we sign up for when it renews.
      </p>
    </div>
  )
}
